// Query snippets library — cloud port of the desktop snippets feature.
// Mirrors the JSON shapes from crates/tres-api/src/handlers/snippets.rs.

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';

export interface SnippetDef {
  id: string;
  name: string;
  content: string;
  description: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface SnippetListResponse {
  snippets: SnippetDef[];
}

export function useSnippets() {
  const { token, ready } = useApiSession();
  return useQuery<SnippetListResponse>({
    queryKey: ['snippets', token ?? 'anon'],
    enabled: ready,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await apiFetch<SnippetListResponse>('/api/v1/snippets', token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

export interface CreateSnippetRequest {
  name: string;
  content: string;
  description?: string;
}

export async function createSnippet(body: CreateSnippetRequest, accessToken: string) {
  return apiFetch<{ id: string }>('/api/v1/snippets', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface PatchSnippetRequest {
  name?: string;
  content?: string;
  description?: string;
}

export async function patchSnippet(id: string, body: PatchSnippetRequest, accessToken: string) {
  return apiFetch<{ id: string }>(`/api/v1/snippets/${encodeURIComponent(id)}`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteSnippet(id: string, accessToken: string) {
  return apiFetch<null>(`/api/v1/snippets/${encodeURIComponent(id)}`, accessToken, {
    method: 'DELETE',
  });
}

/** The address that runs a saved query and returns its rows as CSV. */
export function snippetDataUrl(origin: string, id: string): string {
  return `${origin}/api/v1/snippets/${id}/data?format=csv`;
}
